import { Controller } from "@hotwired/stimulus"
import {
  formatMoney,
  formatThousands,
  stripThousands,
  timeSavedText
} from "../modules/mortgage_tools/format"
import { trackCalculatorUsed, trackShare, trackAdviserCtaClicked } from "../modules/mortgage_tools/ga4_tracker"

const ERROR_CLASSES = ["moneysmart-flash-alert", "mb-6"]
const LAST_INPUTS_KEY = "mortgage_tools.repayment.last_inputs"

export default class extends Controller {
  static targets = [
    "form", "loanAmount", "interestRate", "termYears", "termMonths",
    "paymentFrequency", "submitButton", "results", "errors", "errorMessage",
    "status", "paymentAmount", "paymentFrequencyLabel", "totalPayment",
    "totalInterest", "interestPercentage", "fortnightlyPayment", "interestSaved",
    "timeSaved", "breakdownBody", "shareButton", "shareUrlField", "scheduleLink"
  ]

  static values = {
    calculateUrl: String, shareUrl: String, scheduleUrl: String, autoCalculate: Boolean
  }

  connect() {
    this.formatCurrency()
    this.updateScheduleLink()

    if (this.autoCalculateValue && !new URLSearchParams(window.location.search).size) {
      this.formTarget.requestSubmit()
      return
    }

    if (this.prefillFromUrl()) return

    this.restoreLastInputs()
  }

  formatCurrency() {
    if (this.hasLoanAmountTarget) {
      this.loanAmountTarget.value = formatThousands(this.loanAmountTarget.value)
    }
  }

  async calculate(event) {
    event.preventDefault()
    if (this.calculating) return
    this.calculating = true
    this.formTarget.setAttribute("aria-busy", "true")
    this.hideErrors()

    try {
      const response = await this.postJson(this.calculateUrlValue, this.formValues)
      const data = await response.json()

      if (!response.ok) {
        this.showErrors(data.errors || ["Calculation failed"])
        return
      }

      this.renderResults(data)
      this.updateUrl(this.formValues)
      this.updateScheduleLink()
      this.persistLastInputs()
      trackCalculatorUsed("mortgage_repayment", {
        loanAmount: this.formValues.loan_amount,
        paymentFrequency: data.payment_frequency
      })
    } catch {
      this.showErrors(["Something went wrong. Please try again."])
    } finally {
      this.calculating = false
      this.formTarget.removeAttribute("aria-busy")
    }
  }

  async share() {
    if (this.sharing) return
    this.sharing = true

    try {
      const response = await this.postJson(this.shareUrlValue, this.formValues)
      const data = await response.json()

      if (!response.ok) {
        this.showErrors(data.errors || ["Could not create a share link"])
        return
      }

      const url = new URL(data.url, window.location.origin).toString()
      trackShare("mortgage_repayment", "link")
      await this.presentShareUrl(url)
    } catch {
      this.showErrors(["Something went wrong. Please try again."])
    } finally {
      this.sharing = false
    }
  }

  trackAdviserCta() {
    trackAdviserCtaClicked("mortgage_repayment", "calculator")
  }

  async presentShareUrl(url) {
    try {
      await navigator.clipboard.writeText(url)
      this.setStatus("Share link copied to clipboard")
    } catch {
      this.shareUrlFieldTarget.value = url
      this.shareUrlFieldTarget.classList.remove("hidden")
      this.shareUrlFieldTarget.select()
      this.setStatus("Share link ready to copy")
    }
  }

  postJson(url, body) {
    return fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
        "X-CSRF-Token": this.csrfToken
      },
      body: JSON.stringify(body)
    })
  }

  get formValues() {
    return {
      loan_amount: stripThousands(this.loanAmountTarget.value),
      interest_rate: this.interestRateTarget.value,
      term_years: this.termYearsTarget.value,
      term_months: this.termMonthsTarget.value,
      payment_frequency: this.paymentFrequencyTarget.value
    }
  }

  renderResults(data) {
    this.paymentAmountTarget.textContent = formatMoney(data.payment_amount)
    this.paymentFrequencyLabelTarget.textContent = data.payment_frequency
    this.totalPaymentTarget.textContent = formatMoney(data.total_payment)
    this.totalInterestTarget.textContent = formatMoney(data.total_interest)
    this.interestPercentageTarget.textContent = `${data.interest_percentage}%`

    const fc = data.fortnightly_comparison
    if (fc && this.hasFortnightlyPaymentTarget) {
      this.fortnightlyPaymentTarget.textContent = formatMoney(fc.fortnightly_payment)
      this.interestSavedTarget.textContent = formatMoney(fc.interest_saved)
      this.timeSavedTarget.textContent = timeSavedText(fc.years_saved, fc.months_saved)
    }

    this.renderBreakdown(data.yearly_breakdown || [])
    this.resultsTarget.classList.remove("hidden")
    this.setStatus(
      `Repayments calculated: ${formatMoney(data.payment_amount)} ${data.payment_frequency}`
    )
  }

  renderBreakdown(rows) {
    this.breakdownBodyTarget.innerHTML = rows.map(row => `
      <tr class="border-b border-stone-100 hover:bg-stone-50">
        <td class="px-4 py-2 text-stone-700">${row.year}</td>
        <td class="px-4 py-2 text-right text-stone-600">${formatMoney(row.principal_paid)}</td>
        <td class="px-4 py-2 text-right text-emerald-600">${formatMoney(row.interest_paid)}</td>
        <td class="px-4 py-2 text-right money-value">${formatMoney(row.balance)}</td>
      </tr>
    `).join("")
  }

  updateScheduleLink() {
    if (!this.hasScheduleLinkTarget || !this.hasScheduleUrlValue) return

    const qs = new URLSearchParams(this.formValues).toString()
    this.scheduleLinkTarget.href = qs ? `${this.scheduleUrlValue}?${qs}` : this.scheduleUrlValue
  }

  updateUrl(params) {
    const url = new URL(window.location)
    Object.entries(params).forEach(([key, val]) => {
      if (val !== "" && val !== null && val !== undefined) {
        url.searchParams.set(key, val)
      } else {
        url.searchParams.delete(key)
      }
    })
    window.history.replaceState({}, "", url)
  }

  // Returns true when a recognised field was found in the query string (and
  // the form was submitted with it). A query string can be present without
  // any recognised field — e.g. the installed-app "?source=pwa" marker — in
  // which case this returns false so connect() falls through to the stored
  // last-used inputs instead of leaving the form on its untouched defaults.
  prefillFromUrl() {
    const params = new URLSearchParams(window.location.search)
    if (params.size === 0) return false

    const fields = {
      loan_amount: this.loanAmountTarget,
      interest_rate: this.interestRateTarget,
      term_years: this.termYearsTarget,
      term_months: this.termMonthsTarget,
      payment_frequency: this.paymentFrequencyTarget
    }

    let hasValues = false
    Object.entries(fields).forEach(([key, el]) => {
      const val = params.get(key)
      if (val !== null) {
        el.value = val
        hasValues = true
      }
    })

    if (!hasValues) return false

    this.formatCurrency()
    this.formTarget.requestSubmit()
    return true
  }

  persistLastInputs() {
    try {
      window.localStorage.setItem(LAST_INPUTS_KEY, JSON.stringify(this.formValues))
    } catch {
      // Storage unavailable (e.g. private browsing) — skip persistence.
    }
  }

  restoreLastInputs() {
    let stored
    try {
      stored = window.localStorage.getItem(LAST_INPUTS_KEY)
    } catch {
      return
    }
    if (!stored) return

    let values
    try {
      values = JSON.parse(stored)
    } catch {
      return
    }
    if (!values || typeof values !== "object") return

    const fields = {
      loan_amount: this.loanAmountTarget,
      interest_rate: this.interestRateTarget,
      term_years: this.termYearsTarget,
      term_months: this.termMonthsTarget,
      payment_frequency: this.paymentFrequencyTarget
    }

    let hasValues = false
    Object.entries(fields).forEach(([key, el]) => {
      const val = values[key]
      if (val !== undefined && val !== null) {
        el.value = val
        hasValues = true
      }
    })

    if (!hasValues) return

    this.formatCurrency()
    this.formTarget.requestSubmit()
  }

  setStatus(text) {
    if (this.hasStatusTarget) this.statusTarget.textContent = text
  }

  showErrors(messages) {
    this.setStatus("")
    this.resultsTarget.classList.add("hidden")
    this.errorMessageTarget.textContent = messages.join(". ")
    this.errorsTarget.classList.add(...ERROR_CLASSES)
  }

  hideErrors() {
    this.errorMessageTarget.textContent = ""
    this.errorsTarget.classList.remove(...ERROR_CLASSES)
  }

  get csrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta ? meta.content : ""
  }
}
